import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { fetchBill, type BillApprovalError } from "@/api/public";

type Stage =
  | { kind: "loading" }
  | { kind: "ready"; signedUrl: string }
  | { kind: "missing" }
  | { kind: "error"; error: BillApprovalError };

export function BillPdfPreviewPage() {
  const { token } = useParams<{ token: string }>();
  const [stage, setStage] = useState<Stage>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setStage({ kind: "missing" });
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await fetchBill(token);
      if (cancelled) return;
      if (!result.ok) {
        setStage({ kind: "error", error: result.error });
        return;
      }
      if (!result.data.pdfSignedUrl) {
        setStage({ kind: "missing" });
        return;
      }
      setStage({ kind: "ready", signedUrl: result.data.pdfSignedUrl });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    document.title = "Bill document";
    return () => {
      document.title = "Dolfin Pay";
    };
  }, []);

  if (stage.kind === "loading") {
    return <CenteredSpinner />;
  }
  if (stage.kind === "ready") {
    return (
      <iframe
        src={stage.signedUrl}
        title="Bill document"
        loading="lazy"
        className="h-screen w-screen border-0"
      />
    );
  }
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="max-w-md">
        <CardHeader className="items-center gap-3 text-center">
          <AlertTriangle className="h-10 w-10 text-muted-foreground" />
          <CardTitle>Document not available</CardTitle>
          <CardDescription>
            {stage.kind === "missing"
              ? "There's no original document attached to this bill."
              : "We couldn't load the bill document."}
          </CardDescription>
        </CardHeader>
        <CardContent />
      </Card>
    </div>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner />
    </div>
  );
}
